import { useTranslation } from 'react-i18next';

import type { EtiquetaAyuda, PasoAyuda, PreguntaAyuda } from './pasos';

/** El momento de «Importar extracto» que se ve (cada uno con su ayuda). */
export type MomentoImportar =
  | 'elegir'
  | 'comprobar'
  | 'columnas'
  | 'vista'
  | 'hecho';

const FILA = '[data-testid="importar-fila"]';

function uno(selector: string): Element[] {
  const el = document.querySelector(selector);
  return el ? [el] : [];
}

function testid(id: string): Element[] {
  return uno(`[data-testid="${id}"]`);
}

/** La fila de ejemplo: una nueva o, si no hay, la primera. */
function filaEjemplo(): Element[] {
  const fila =
    document.querySelector(`${FILA}[data-ayuda-fila="nuevo"]`) ??
    document.querySelector(FILA);
  return fila ? [fila] : [];
}

function filaRepetida(): Element[] {
  return uno(`${FILA}[data-ayuda-fila="repetido"]`);
}

function etiquetasFila(t: (texto: string) => string): EtiquetaAyuda[] {
  const fila = filaEjemplo()[0];
  if (!fila) {
    return [];
  }
  const partes: [string, string, boolean][] = [
    ['[data-ayuda="concepto"]', t('Description'), false],
    ['[data-ayuda="categoria"]', t('Category'), true],
    ['[data-ayuda="importe"]', t('Amount'), false],
  ];
  return partes.flatMap(([selector, texto, debajo]) => {
    const el = fila.querySelector(selector);
    return el ? [{ el, texto, debajo }] : [];
  });
}

/** El fallo que cuenta el bocadillo de «Elegir», si lo hay. */
function fallo(error: string): Element[] {
  return uno(`[data-testid="importar-bocadillo"][data-ayuda-error="${error}"]`);
}

/**
 * La ayuda de «Importar extracto» (maqueta aprobada por Vadik el
 * 27-oct-2026). La pantalla cambia de momento (elegir el archivo,
 * comprobarlo, columnas, qué es nuevo, hecho) y Gavi explica el que se ve:
 * cada momento tiene su recorrido y sus preguntas, y lo que no está en
 * pantalla se salta solo. Gavi solo señala: nunca elige el archivo, ni
 * contesta las dudas, ni trae ni deshace nada.
 */
export function usePreguntasImportar(
  momento: MomentoImportar,
): PreguntaAyuda[] {
  const { t } = useTranslation();

  const paso = (
    id: string,
    objetivo: () => Element[],
    texto: string,
    extra: Partial<PasoAyuda> = {},
  ): PasoAyuda => ({
    id,
    escena: 'normal',
    radio: 14,
    texto,
    objetivo,
    ...extra,
  });
  const hola = (texto: string) =>
    paso('hola', () => uno('[data-ayuda="titulo-importar"]'), texto, {
      radio: 12,
    });
  // Dos finales (Vadik, 27-oct-2026): uno mientras se prepara el archivo y
  // otro cuando ya se ve lo que entra.
  const finalOrdenar = paso(
    'final',
    () => testid('ojo-de-buey'),
    t(
      'Done! Tap the porthole whenever you want to go over it again. Sorting statements relaxes me. Other seagulls scream at five in the morning; we all have our vices.',
    ),
    { radio: 24, final: true },
  );
  const finalPescado = paso(
    'final',
    () => testid('ojo-de-buey'),
    t(
      'Done! Tap the porthole whenever you want to go over it again. My statement is shorter: fish, fish, stolen croquette, fish.',
    ),
    { radio: 24, final: true },
  );
  const saludo = t(
    'Norma 43 is a format. I thought it was a seagull from my colony.',
  );

  const todo = (pasos: PasoAyuda[]): PreguntaAyuda => ({
    id: 'todo',
    texto: t('Tell me about this screen'),
    pasos,
  });

  // Lo que ya estaba: solo se salta la misma línea del banco; lo demás se
  // pregunta (Vadik, 27-oct-2026: «que pregunte siempre»).
  const loQueTenia: PreguntaAyuda = {
    id: 'ya-tenia',
    texto: t('What happens to what I already had?'),
    pasos: [
      paso(
        'compara',
        () => [...testid('importar-bocadillo'), ...testid('importar-cifras')],
        t(
          'I compare each transaction in the file with what is already there. If a line already came in the same way with another statement (same day, amount and description), I do not repeat it.',
        ),
      ),
      paso(
        'tachado',
        filaRepetida,
        t(
          'Those show up crossed out, with "You already had it: it won\'t be repeated", and they do not come in.',
        ),
        { radio: 12 },
      ),
      paso(
        'pregunto',
        () => testid('importar-dudas'),
        t(
          'If it looks like something you entered yourself, I do not decide: I ask you on these cards.',
        ),
        { radio: 16, final: true },
      ),
    ],
  };

  switch (momento) {
    case 'elegir':
      return [
        todo([
          hola(
            t('Hi! Here you bring the file from your bank and I sort it out.') +
              ' ' +
              t('I will tell you step by step.') +
              ' ' +
              saludo,
          ),
          paso(
            'que-hago',
            () =>
              uno('[data-testid="importar-bocadillo"]:not([data-ayuda-error])'),
            t(
              'Choose the file and I put it into this account. If something looks like what you already entered, I ask you: I do not decide.',
            ),
            { radio: 16 },
          ),
          paso(
            'fallo',
            () => uno('[data-testid="importar-bocadillo"][data-ayuda-error]'),
            t(
              'Here I tell you what went wrong. Nothing has been saved: try another file, or download it again in another format.',
            ),
            { radio: 16 },
          ),
          paso(
            'formatos',
            () => testid('importar-formatos'),
            t(
              'CSV (a text file), Excel and Norma 43 work. Also OFX, QIF and CAMT, if your bank offers them. Any of them is fine.',
            ),
            { radio: 16 },
          ),
          paso(
            'descargar',
            () => testid('importar-ayuda'),
            t(
              'Not sure where to download it? This card explains it in four steps. It is usually on the bank website, in the account transactions.',
            ),
            { radio: 16 },
          ),
          paso(
            'privacidad',
            () => testid('importar-privacidad'),
            t(
              'The file is read on your phone and is not kept. Only the transactions you bring stay.',
            ),
            { radio: 12 },
          ),
          paso(
            'elegir',
            () => testid('importar-elegir'),
            t(
              'Once you have downloaded it, "Choose file" opens your folders. You choose it; I only point it out.',
            ),
            { radio: 16 },
          ),
          finalOrdenar,
        ]),
        {
          id: 'archivo',
          texto: t('What file do I need and where do I download it?'),
          pasos: [
            paso(
              'que-archivo',
              () => testid('importar-formatos'),
              t(
                'Any file with the account transactions works: CSV (text), Excel or Norma 43, a statement format of many Spanish banks. Also OFX, QIF and CAMT.',
              ),
              { radio: 16 },
            ),
            paso(
              'donde',
              () => testid('importar-ayuda'),
              t(
                'Download it from your bank website or app: look for the account transactions and a "Download" or "Export" button. This card explains it step by step.',
              ),
              { radio: 16 },
            ),
            paso(
              'fechas',
              () => testid('importar-ayuda'),
              t(
                'Choose the dates you are missing and the format. On the phone, the file usually ends up in "Downloads": you choose it from there.',
              ),
              { radio: 16 },
            ),
            paso(
              'elegirlo',
              () => testid('importar-elegir'),
              t(
                'With the file downloaded, tap "Choose file" and look for it. I do not open your folders: you do that.',
              ),
              { radio: 16, final: true },
            ),
          ],
        },
        {
          id: 'no-lee',
          texto: t('Why does it not read my file?'),
          pasos: [
            paso(
              'formato',
              () => fallo('formato'),
              t(
                'I do not know how to read that kind of file. Download it again as CSV, Excel, Norma 43, OFX, QIF or CAMT, whichever your bank offers.',
              ),
              { radio: 16, final: true },
            ),
            paso(
              'excel-antiguo',
              () => fallo('excel-antiguo'),
              t(
                'It is an old Excel file (.xls). Open it and save it as .xlsx or CSV, or download it again in another format, and choose it again.',
              ),
              { radio: 16, final: true },
            ),
            paso(
              'vacio',
              () => fallo('vacio'),
              t(
                'I cannot find transactions in that file. Check that it is the account transactions file, with dates and amounts.',
              ),
              { radio: 16, final: true },
            ),
            paso(
              'ilegible',
              () => fallo('ilegible'),
              t(
                'I could not read it. Try downloading it again or in another format. Nothing has been saved.',
              ),
              { radio: 16, final: true },
            ),
          ],
        },
        loQueTenia,
      ];
    case 'comprobar':
      return [
        todo([
          hola(
            t(
              'Hi! Here you check that I have read your file properly before bringing anything.',
            ) +
              ' ' +
              t('I will tell you step by step.') +
              ' ' +
              saludo,
          ),
          paso(
            'archivo',
            () => testid('importar-archivo'),
            t(
              'This is your file, with the transactions I found and the dates from the first to the last. Check that they sound right.',
            ),
            { radio: 16 },
          ),
          paso(
            'muestra',
            () => testid('importar-muestra-tabla'),
            t(
              'This is how I understood the first three lines: date, description and amount. Expenses have a minus. Compare them with your file or the bank app.',
            ),
            { radio: 16 },
          ),
          paso(
            'seguir',
            () => testid('importar-bien'),
            t(
              'If everything matches, "Yes, that\'s right". Nothing is saved yet: first I show you what is new and what you already had.',
            ),
            { radio: 16 },
          ),
          paso(
            'columnas',
            () => testid('importar-cambiar-columnas'),
            t(
              'If something does not match, "No, change columns" lets you choose which column is the date, the description and the amount.',
            ),
            { radio: 16 },
          ),
          paso(
            'recordar',
            () => testid('importar-recordare'),
            t(
              'When you bring the transactions, I remember these columns for this account and you will not have to choose them again.',
            ),
            { radio: 10 },
          ),
          finalOrdenar,
        ]),
        {
          id: 'leido',
          texto: t('How do I know it was read properly?'),
          pasos: [
            paso(
              'cuantos',
              () => testid('importar-archivo'),
              t(
                'Look at how many transactions I found and from which date to which. It should look like what you downloaded.',
              ),
              { radio: 16 },
            ),
            paso(
              'tres',
              () => testid('importar-muestra-tabla'),
              t(
                'Compare these three lines with your file: the date (I read it as day/month/year), the description and the amount, with a minus if it is an expense.',
              ),
              { radio: 16 },
            ),
            paso(
              'si-no',
              () => [
                ...testid('importar-bien'),
                ...testid('importar-cambiar-columnas'),
              ],
              t(
                'If it matches, "Yes, that\'s right". If not, "No, change columns", only in CSV and Excel: the other formats already bring the data in order.',
              ),
              { radio: 16, final: true },
            ),
          ],
        },
      ];
    case 'columnas':
      return [
        todo([
          hola(
            t('Hi! Here you tell me which column of your file is each thing.') +
              ' ' +
              t('I will tell you step by step.') +
              ' ' +
              saludo,
          ),
          paso(
            'importes',
            () => testid('importar-modo-segmentado'),
            t(
              'First, how the amounts come: in one column with a sign ("One amount") or in two: "Money out" (what leaves) and "Money in" (what comes in).',
            ),
          ),
          paso(
            'tabla',
            () => testid('importar-columnas-tabla'),
            t(
              'Tap each row to choose which column that data comes from. You will see the column letter and an example of what it holds.',
            ),
            { radio: 16 },
          ),
          paso(
            'cuenta',
            () => testid('importar-columnas-cuenta'),
            t(
              'Here you see how many transactions come out with your choices. If it says 0, try another column.',
            ),
            { radio: 10 },
          ),
          paso(
            'listo',
            () => testid('importar-columnas-listo'),
            t(
              'When the ones you expect come out, "Use these columns". I will show you the first lines again so you can check it.',
            ),
            { radio: 16 },
          ),
          finalOrdenar,
        ]),
      ];
    case 'vista':
      return [
        todo([
          hola(
            t(
              'Hi! Here you see what is new and what you already had, before saving anything.',
            ) +
              ' ' +
              t('I will tell you step by step.') +
              ' ' +
              saludo,
          ),
          paso(
            'resumen',
            () => testid('importar-cifras'),
            t(
              'At the top, the summary: how many are new, how many already have a category and how many you already had.',
            ),
            { radio: 16 },
          ),
          paso(
            'dudas',
            () => testid('importar-dudas'),
            t(
              'These cards compare a transaction from the file with one you entered yourself. I do not decide: you tell me if it is the same or two charges.',
            ),
            { radio: 16 },
          ),
          paso(
            'tarjeta',
            () => testid('importar-duda'),
            t(
              'At the top, the one from the file ("Bank"); below, the one you entered. The coloured label says why they look alike: amount, date or account.',
            ),
            {
              radio: 16,
              etiquetas: () => {
                const el = document.querySelector(
                  '[data-testid="importar-duda-motivo"]',
                );
                return el ? [{ el, texto: t('Why they look alike') }] : [];
              },
            },
          ),
          paso(
            'botones',
            () => [
              ...testid('importar-duda-mismo'),
              ...testid('importar-duda-distinto'),
            ],
            t(
              'With "It\'s the same", when bringing them the one from the file comes in and yours is removed. With "They\'re different", both stay.',
            ),
          ),
          paso(
            'fila',
            filaEjemplo,
            t(
              'Each row is a transaction from the file: description, the category I would give it and the amount. Expenses have a minus.',
            ),
            { radio: 12, etiquetas: () => etiquetasFila(t) },
          ),
          paso(
            'categoria',
            filaEjemplo,
            t(
              'The category comes from a rule: a note like "this payee goes in this category". If it says "No category", you choose it later in "To review".',
            ),
            { radio: 12 },
          ),
          paso(
            'repetido',
            filaRepetida,
            t(
              'If it says "You already had it: it won\'t be repeated", that very bank line already came in with another statement: same day, amount and description. It does not come in again.',
            ),
            { radio: 12 },
          ),
          paso(
            'historia',
            () => testid('importar-historia'),
            t(
              'These are transactions from before your first month with Rumbo. "Bring the history" puts them in separately and does not change today\'s balance or your months.',
            ),
            { radio: 16 },
          ),
          paso(
            'anteriores',
            () => testid('importar-anteriores'),
            t(
              'The ones from the date of your starting balance or earlier are already counted in that balance, the amount you opened the account with. I leave them out.',
            ),
            { radio: 16 },
          ),
          paso(
            'fuera',
            () => testid('importar-pista-fuera'),
            t(
              'If you do not want to bring one, tap it and it stays out. Tap it again to bring it. Nothing is saved until the button below.',
            ),
            { radio: 10 },
          ),
          paso(
            'traer',
            () => testid('importar-traer'),
            t(
              'Nothing has been saved so far. "Bring" puts the new ones into the account. If there are cards left unanswered, the button waits.',
            ),
            { radio: 16 },
          ),
          finalPescado,
        ]),
        {
          id: 'mismo',
          texto: t('It asks me if it is the same: what do I answer?'),
          pasos: [
            paso(
              'tarjeta',
              () => testid('importar-duda'),
              t(
                'Each card shows the transaction from the file ("Bank") and the one you entered, with its date and amount. The coloured label says why they look alike.',
              ),
              { radio: 16 },
            ),
            paso(
              'motivo',
              () => testid('importar-duda-motivo'),
              t(
                'They can look alike because of the amount ("Similar amount"), the date ("Same amount, N days apart") or because it is in another account ("Same amount, in…").',
              ),
              { radio: 10 },
            ),
            paso(
              'es-el-mismo',
              () => testid('importar-duda-mismo'),
              t(
                '"It\'s the same": when bringing them, the one from the file comes in and the one you entered is removed. Your name goes to its notes and your category stays.',
              ),
            ),
            paso(
              'son-distintos',
              () => testid('importar-duda-distinto'),
              t(
                '"They\'re different": they are two real charges. Both stay, the one you entered and the one from the file. If in doubt, this one deletes nothing.',
              ),
            ),
            paso(
              'hasta-traer',
              () => testid('importar-traer'),
              t(
                'Until you tap "Bring", nothing changes and you can change your mind. The button waits until you answer all the cards.',
              ),
              { radio: 16, final: true },
            ),
          ],
        },
        {
          id: 'iguales',
          texto: t('What if there are two identical transactions?'),
          pasos: [
            paso(
              'dos-cobros',
              filaEjemplo,
              t(
                'Two identical transactions are two charges. I do not merge or remove any on my own: each line of the file comes in as a transaction.',
              ),
              { radio: 12 },
            ),
            paso(
              'misma-linea',
              filaRepetida,
              t(
                'I only skip a line that already came in exactly the same with another statement: same day, amount and description.',
              ),
              { radio: 12 },
            ),
            paso(
              'distintos',
              () => testid('importar-duda-distinto'),
              t(
                'If what you entered and what is in the file are two equal payments, answer "They\'re different": both stay. I never delete on my own.',
              ),
              { final: true },
            ),
          ],
        },
        {
          id: 'fuera',
          texto: t('Can I leave one out?'),
          pasos: [
            paso(
              'tocalo',
              filaEjemplo,
              t(
                'Yes. Tap a transaction and it stays out, crossed out and with "Left out · tap to bring it". Tap it again and it comes back in.',
              ),
              { radio: 12, disponible: () => !!filaEjemplo().length },
            ),
            paso(
              'se-ajusta',
              () => [...testid('importar-cifras'), ...testid('importar-traer')],
              t(
                'The figures at the top and the button adjust on their own to what you leave in. Nothing is saved until you tap "Bring".',
              ),
              { radio: 16, final: true },
            ),
          ],
        },
        loQueTenia,
      ];
    default:
      // 'hecho'
      return [
        todo([
          hola(
            t(
              'Hi! They are already in. I will tell you what happened and what is left for you.',
            ) +
              ' ' +
              saludo,
          ),
          paso(
            'dentro',
            () => testid('importar-hecho-texto'),
            t(
              'They are in now. They come in unapproved (approving is accepting them as right) and wait in "To review", where you can give them a category.',
            ),
            { radio: 16 },
          ),
          paso(
            'sustituidos',
            () => testid('importar-sustituidos'),
            t(
              'These came in instead of the ones you entered. Your name went into their notes and your category stayed.',
            ),
            { radio: 12 },
          ),
          paso(
            'mismo-comercio',
            () => testid('mismo-comercio'),
            t(
              'If you always put a payee in the same category, I offer to place the new ones there. With "No, I\'ll look at them" they stay as they are.',
            ),
            { radio: 16 },
          ),
          paso(
            'saldo',
            () => testid('importar-saldo'),
            t(
              'This is the account balance now. The new ones change it; the history, if you brought it, does not.',
            ),
            { radio: 16 },
          ),
          paso(
            'adonde',
            () => [
              ...testid('importar-revisar'),
              ...testid('importar-mas-tarde'),
            ],
            t(
              '"Review them now" takes you to "To review". "Later" takes you back to the account: the new ones will keep waiting.',
            ),
            { radio: 16 },
          ),
          paso(
            'deshacer',
            () => testid('importar-deshacer'),
            t(
              '"Undo the import" removes what you have just brought. It is only on this screen: once you leave, it is gone.',
            ),
          ),
          finalPescado,
        ]),
        {
          id: 'equivoco',
          texto: t('What if I make a mistake?'),
          pasos: [
            paso(
              'deshacer',
              () => testid('importar-deshacer'),
              t(
                '"Undo the import" removes what you have just brought. If you had said "It\'s the same", what you entered comes back too.',
              ),
            ),
            paso(
              'historia',
              () => uno('[data-testid="importar-hecho"][data-historia="true"]'),
              t(
                'If you brought the history there is no one-tap "Undo", because it changes several things at once: the starting balance and the months.',
              ),
              { radio: 16 },
            ),
            paso(
              'corregir',
              () => testid('importar-hecho-texto'),
              t(
                'What is already in is fixed like any transaction: open it in "To review" or in "Transactions" and change it.',
              ),
              { radio: 16, final: true },
            ),
          ],
        },
        {
          id: 'ahora',
          texto: t('What do I do now that it is in?'),
          pasos: [
            paso(
              'por-revisar',
              () => testid('importar-hecho-texto'),
              t(
                'Everything new waits in "To review", with a category or not. Reviewing is looking at each transaction and approving it, which is accepting it as right.',
              ),
              { radio: 16 },
            ),
            paso(
              'revisar',
              () => [
                ...testid('importar-revisar'),
                ...testid('importar-mas-tarde'),
              ],
              t(
                '"Review them now" takes you there. "Later" takes you back to the account: the new ones keep waiting and you will see how many are left on Home.',
              ),
              { radio: 16, final: true },
            ),
          ],
        },
      ];
  }
}
